import React, { memo, useCallback, useRef, useState } from "react";
import "./AttachmentUploader.scss";

/*
 * Drag-and-drop file picker extracted from
 * pages/providerPayRequestDashboard/components/AddPayRequestDialog.jsx so more
 * than one form can use it. Selection only — uploading is the caller's job;
 * pass per-file progress through `statuses`.
 */

const DEFAULT_ACCEPT = ".jpg,.jpeg,.png,.pdf";
const BYTES_PER_MB = 1024 * 1024;

const FileGlyph = ({ stroke = "#9CA3AF", size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <polyline points="14,2 14,8 20,8" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AttachmentUploader = ({
  files = [],
  onFilesAdd,
  onRemove,
  statuses = {},
  accept = DEFAULT_ACCEPT,
  maxSizeMB = 10,
  hint = "Only .jpg, .png and .pdf files supported. 10 mb each max file size",
  icon,
  disabled = false,
}) => {
  const inputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState("");

  const allowedExtensions = accept
    .split(",")
    .map((ext) => ext.trim().toLowerCase())
    .filter(Boolean);

  const validateFile = useCallback(
    (file) => {
      const name = (file.name || "").toLowerCase();
      const extensionOk = allowedExtensions.some((ext) => name.endsWith(ext));

      if (!extensionOk) {
        return `${file.name}: only ${allowedExtensions.join(", ")} files are supported.`;
      }
      if (file.size > maxSizeMB * BYTES_PER_MB) {
        return `${file.name}: exceeds the ${maxSizeMB} MB limit.`;
      }
      return null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accept, maxSizeMB]
  );

  const addFiles = useCallback(
    (fileList) => {
      const incoming = Array.from(fileList || []);
      if (incoming.length === 0) return;

      const accepted = [];
      const errors = [];

      incoming.forEach((file) => {
        const message = validateFile(file);
        if (message) {
          errors.push(message);
        } else {
          accepted.push(file);
        }
      });

      setError(errors.join(" "));

      if (accepted.length > 0) {
        onFilesAdd?.(accepted);
      }
    },
    [onFilesAdd, validateFile]
  );

  const handleBrowse = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleInputChange = (e) => {
    addFiles(e.target.files);
    // Allow re-selecting the same file after a removal.
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    addFiles(e.dataTransfer.files);
  };

  return (
    <div className="attachment-uploader">
      <div
        className={`attachment-uploader__area ${isDragOver ? "drag-over" : ""} ${
          disabled ? "attachment-uploader__area--disabled" : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowse}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleBrowse();
          }
        }}
      >
        <div className="attachment-uploader__content">
          <span className="attachment-uploader__icon-box">
            {icon ?? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M21.44 11.05L12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
          <span className="attachment-uploader__text">
            <span className="attachment-uploader__title">Add Attachment</span>
            <span className="attachment-uploader__hint">{hint}</span>
          </span>
          <span className="attachment-uploader__plus" aria-hidden="true">
            +
          </span>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        onChange={handleInputChange}
        style={{ display: "none" }}
      />

      {error && <p className="attachment-uploader__error">{error}</p>}

      {files.length > 0 && (
        <div className="attachment-uploader__files">
          {files.map((file) => (
            <div key={file.id ?? file.name} className="attachment-uploader__chip">
              <span className="attachment-uploader__file-info">
                <FileGlyph stroke="#6B7280" size={16} />
                <span className="attachment-uploader__file-name">{file.name}</span>
              </span>
              <span className="attachment-uploader__file-status">
                {statuses[file.id] === "uploading" && <span className="attachment-uploader__loader" />}
                {statuses[file.id] === "success" && <span className="status success">✓</span>}
                {statuses[file.id] === "error" && <span className="status error">✗</span>}
              </span>
              <button
                type="button"
                className="attachment-uploader__remove"
                onClick={() => onRemove?.(file.id ?? file.name)}
                aria-label={`Remove ${file.name}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(AttachmentUploader);
